class RrgAnnouncements extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-announcement]')];
    this.countdown = this.querySelector('[data-announcement-countdown]');
    this.index = 0;
    this.updateCountdowns();
    this.updateCountdownVisibility();

    if (this.items.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const interval = Number(this.dataset.interval) || 4000;
      this.timer = window.setInterval(() => this.show(this.index + 1), interval);
    }

    this.countdownTimer = window.setInterval(() => this.updateCountdowns(), 60000);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
    window.clearInterval(this.countdownTimer);
  }

  show(next) {
    if (!this.items.length) return;
    this.items[this.index]?.classList.remove('is-active');
    this.index = next % this.items.length;
    this.items[this.index]?.classList.add('is-active');
    this.updateCountdownVisibility();
  }

  updateCountdownVisibility() {
    if (!this.countdown) return;
    const visible = this.index === 0;
    this.countdown.classList.toggle('is-hidden', !visible);
    this.countdown.setAttribute('aria-hidden', String(!visible));
  }

  updateCountdowns() {
    this.querySelectorAll('[data-countdown]').forEach((node) => {
      const end = new Date(node.dataset.countdown);
      if (Number.isNaN(end.getTime())) return;
      const days = Math.max(0, Math.floor((end.getTime() - Date.now()) / 86400000));
      node.textContent = String(days);
    });
  }
}

if (!customElements.get('rrg-announcements')) {
  customElements.define('rrg-announcements', RrgAnnouncements);
}

const rrgReveal = () => {
  const elements = [...document.querySelectorAll('[data-rrg-reveal]:not(.rrg-reveal-ready)')];
  if (!elements.length) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    elements.forEach((element) => element.classList.add('rrg-is-visible', 'rrg-reveal-ready'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('rrg-is-visible');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
  );

  elements.forEach((element) => {
    element.classList.add('rrg-reveal-ready');
    observer.observe(element);
  });
};

document.addEventListener('DOMContentLoaded', rrgReveal);
document.addEventListener('shopify:section:load', rrgReveal);

const RRG_WISHLIST_KEY = 'rrg:wishlist';
let rrgWishlistItems = [];

const rrgReadWishlist = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(RRG_WISHLIST_KEY) || '[]');
    return Array.isArray(stored) ? stored.filter((item) => item?.handle && item?.url && item?.title) : [];
  } catch (error) {
    return [];
  }
};

const rrgWriteWishlist = () => {
  try {
    localStorage.setItem(RRG_WISHLIST_KEY, JSON.stringify(rrgWishlistItems));
  } catch (error) {
    // Keep the current page state useful when browser storage is unavailable.
  }
};

const rrgWishlistProductFromButton = (button) => ({
  handle: button.dataset.productHandle,
  title: button.dataset.productTitle,
  url: button.dataset.productUrl,
  image: button.dataset.productImage || '',
  price: button.dataset.productPrice || '',
});

const rrgRenderWishlist = () => {
  const drawer = document.querySelector('[data-rrg-wishlist-drawer]');
  const list = drawer?.querySelector('[data-rrg-wishlist-items]');
  const empty = drawer?.querySelector('[data-rrg-wishlist-empty]');
  const count = document.querySelector('[data-rrg-wishlist-count]');
  const trigger = document.querySelector('[data-rrg-wishlist-trigger]');
  const savedHandles = new Set(rrgWishlistItems.map((item) => item.handle));

  document.querySelectorAll('[data-rrg-wishlist-product]').forEach((button) => {
    const active = savedHandles.has(button.dataset.productHandle);
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', String(active));
    button.setAttribute('aria-label', active ? button.dataset.removeLabel : button.dataset.addLabel);
  });

  if (count) {
    count.textContent = String(rrgWishlistItems.length);
    count.hidden = rrgWishlistItems.length === 0;
  }
  if (trigger) trigger.classList.toggle('is-active', rrgWishlistItems.length > 0);
  if (!list || !empty) return;

  list.replaceChildren();
  empty.hidden = rrgWishlistItems.length > 0;
  rrgWishlistItems.forEach((item) => {
    const row = document.createElement('li');
    row.className = 'rrg-wishlist-item';

    if (item.image) {
      const image = document.createElement('img');
      image.className = 'rrg-wishlist-item__image';
      image.src = item.image;
      image.alt = '';
      image.loading = 'lazy';
      row.append(image);
    } else {
      const imagePlaceholder = document.createElement('span');
      imagePlaceholder.className = 'rrg-wishlist-item__image';
      imagePlaceholder.setAttribute('aria-hidden', 'true');
      row.append(imagePlaceholder);
    }

    const link = document.createElement('a');
    link.className = 'rrg-wishlist-item__link';
    link.href = item.url;
    const title = document.createElement('span');
    title.textContent = item.title;
    link.append(title);
    if (item.price) {
      const price = document.createElement('span');
      price.className = 'rrg-wishlist-item__price';
      price.textContent = item.price;
      link.append(price);
    }
    row.append(link);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'rrg-wishlist-item__remove';
    remove.dataset.rrgWishlistRemove = item.handle;
    remove.setAttribute('aria-label', `${drawer.dataset.removeLabel}: ${item.title}`);
    remove.textContent = '×';
    row.append(remove);
    list.append(row);
  });
};

const rrgInitWishlist = () => {
  rrgWishlistItems = rrgReadWishlist();

  document.querySelectorAll('[data-rrg-wishlist-product]').forEach((button) => {
    try {
      const legacyKey = `rrg-wishlist:${button.dataset.productHandle}`;
      const isLegacySaved = localStorage.getItem(legacyKey) === 'true';
      const isAlreadySaved = rrgWishlistItems.some((item) => item.handle === button.dataset.productHandle);
      if (isLegacySaved && !isAlreadySaved) rrgWishlistItems.push(rrgWishlistProductFromButton(button));
      if (isLegacySaved) localStorage.removeItem(legacyKey);
    } catch (error) {
      // Ignore legacy migration when storage is unavailable.
    }
  });

  rrgWriteWishlist();
  rrgRenderWishlist();
};

document.addEventListener('DOMContentLoaded', rrgInitWishlist);
document.addEventListener('shopify:section:load', rrgInitWishlist);

document.addEventListener('click', (event) => {
  const closeDrawer = event.target.closest('[data-rrg-close-drawer]');
  if (closeDrawer) {
    closeDrawer.closest('details')?.removeAttribute('open');
    return;
  }

  const wishlistProduct = event.target.closest('[data-rrg-wishlist-product]');
  if (wishlistProduct) {
    const product = rrgWishlistProductFromButton(wishlistProduct);
    const existingIndex = rrgWishlistItems.findIndex((item) => item.handle === product.handle);
    if (existingIndex >= 0) rrgWishlistItems.splice(existingIndex, 1);
    else rrgWishlistItems.unshift(product);
    rrgWriteWishlist();
    rrgRenderWishlist();
    return;
  }

  const wishlistRemove = event.target.closest('[data-rrg-wishlist-remove]');
  if (wishlistRemove) {
    rrgWishlistItems = rrgWishlistItems.filter((item) => item.handle !== wishlistRemove.dataset.rrgWishlistRemove);
    rrgWriteWishlist();
    rrgRenderWishlist();
    return;
  }

  const wishlistTrigger = event.target.closest('[data-rrg-wishlist-trigger]');
  if (wishlistTrigger) {
    const drawer = document.querySelector('[data-rrg-wishlist-drawer]');
    if (drawer?.showModal) {
      rrgRenderWishlist();
      drawer.showModal();
      wishlistTrigger.setAttribute('aria-expanded', 'true');
    }
    return;
  }

  const wishlistClose = event.target.closest('[data-rrg-wishlist-close]');
  if (wishlistClose) {
    wishlistClose.closest('dialog')?.close();
    document.querySelector('[data-rrg-wishlist-trigger]')?.setAttribute('aria-expanded', 'false');
  }
});

document.addEventListener('close', (event) => {
  if (!event.target.matches?.('[data-rrg-wishlist-drawer]')) return;
  document.querySelector('[data-rrg-wishlist-trigger]')?.setAttribute('aria-expanded', 'false');
}, true);
